import useReactRouter from 'use-react-router';

import { getDefaultDataFillOutFlow } from 'configs/routes';
import { nextStep, Step, setStep } from 'actions/step.action';
import { RouteParams } from 'components/custom-route/custom-route';
import { useProduct } from 'providers/product.provider';
import { DocumentType, Signer } from 'generated/credit-analysis-graphql';
import { SITE_NITEROI_URL, SITE_URL } from 'env';

type State = {
    allowedDocuments?: DocumentType[];
    typeGroup?: string;
    signer?: Signer;
};

const useNavigation = () => {
    const { history, match } = useReactRouter<RouteParams>();
    const { productDataFillOutFlow, setCurrentScreen } = useProduct();

    const buildUrl = (path: string, loanApplicationId: string): string => {
        return path
            .replace(':loanApplicationId', loanApplicationId)
            .concat(location.search);
    };

    const buildUrlWithSigner = (
        path: string,
        loanApplicationId: string,
        signerId: string
    ): string => {
        return path
            .replace(':loanApplicationId', loanApplicationId)
            .replace(':signerId', signerId)
            .concat(location.search);
    };

    const nextScreen = (current = '') => {
        const currentRoute = current || match.path;
        const routes =
            productDataFillOutFlow.length > 0
                ? productDataFillOutFlow
                : getDefaultDataFillOutFlow();

        routes.forEach((route, index) => {
            const nextRoute = routes[index + 1];
            if (route.path === currentRoute) {
                if (productDataFillOutFlow.length > 0) {
                    setCurrentScreen(nextRoute.path);
                }

                history.push(
                    buildUrl(nextRoute.path, match.params.loanApplicationId)
                );
            }
        });
    };

    const previousScreen = () => {
        history.goBack();
    };

    const goToPreviousRoute = () => {
        const routes =
            productDataFillOutFlow.length > 0
                ? productDataFillOutFlow
                : getDefaultDataFillOutFlow();

        for (let i = routes.length - 1; i >= 0; i--) {
            if (routes[i].path === match.path) {
                history.push(
                    buildUrl(routes[i - 1].path, match.params.loanApplicationId)
                );
            }
        }
    };

    const goToLoanConfiguration = (
        loanApplicationId: string = match.params.loanApplicationId
    ) => {
        history.push(
            buildUrl('/:loanApplicationId/configuracao', loanApplicationId)
        );
    };

    const goToLoanInformation = (
        loanApplicationId: string = match.params.loanApplicationId
    ) => {
        history.push(
            buildUrl('/:loanApplicationId/informacao', loanApplicationId)
        );
    };

    const goToAddressConfiguration = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/endereco',
                match.params.loanApplicationId
            )
        );
    };

    const goToBankAccountConfiguration = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/dados-bancarios',
                match.params.loanApplicationId
            )
        );
    };

    const goToProposalConfirmation = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/confirmacao',
                match.params.loanApplicationId
            )
        );
    };

    const goToSummary = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/resumo',
                match.params.loanApplicationId
            )
        );
    };

    const goToFeedback = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/feedback',
                match.params.loanApplicationId
            )
        );
    };

    const goToPassword = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/senha-de-acesso',
                match.params.loanApplicationId
            )
        );
    };

    const goToError = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/error',
                match.params.loanApplicationId
            )
        );
    };

    const goToBndes = () => {
        history.push(
            buildUrl(
                '/bndes/:loanApplicationId/utm_source=bndes',
                match.params.loanApplicationId
            )
        );
    };

    const goToProposalExpired = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/expirado',
                match.params.loanApplicationId
            )
        );
    };

    const goToLoanDenied = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/negado',
                match.params.loanApplicationId
            )
        );
    };

    const goToLoanCancelled = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/cancelado',
                match.params.loanApplicationId
            )
        );
    };

    const goToPreApprovedHome = () => {
        history.replace(`/pre-aprovado${location.search}`);
    };

    const goToWelcome = () => {
        nextStep();
        history.push(`/bem-vindo${location.search}`);
    };

    const goToAlreadyRegisteredMail = () => {
        history.push('/email-cadastrado');
    };

    const goToBizcapitalSite = () => {
        window.location.href = SITE_URL;
    };

    const goToNiteroiSite = (loanApplicationId: string) => {
        window.location.href = `${SITE_NITEROI_URL}/${loanApplicationId}`;
    };

    // BORROWER BIZON

    const goToShareAntiFraudPartner = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/identificacao/compartilhe-com-socio',
                match.params.loanApplicationId
            )
        );
    };

    const goToSharedLinkSuccessful = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/identificacao/enviado-com-sucesso',
                match.params.loanApplicationId
            )
        );
    };

    const goToAntiFraudCheckout = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/identificacao',
                match.params.loanApplicationId
            )
        );
    };

    const goToPartnerNotIdentified = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/identificacao/socio-nao-identificado',
                match.params.loanApplicationId
            )
        );
    };

    const goToInvalidPartner = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/identificacao/socio-invalido',
                match.params.loanApplicationId
            )
        );
    };

    const goToLivenessStep = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/identificacao/prova-de-vida',
                match.params.loanApplicationId
            )
        );
    };

    const goToCreditWorthyStep = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/identificacao/questionario',
                match.params.loanApplicationId
            )
        );
    };

    const goToDocumentCheckout = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/documentacao',
                match.params.loanApplicationId
            )
        );
    };

    const goToSendCompanyDocuments = (
        allowedDocuments: DocumentType[],
        type: string
    ) => {
        const state: State = { allowedDocuments, typeGroup: type };
        history.push({
            pathname: buildUrl(
                '/:loanApplicationId/documentacao/envio-documentos/',
                match.params.loanApplicationId
            ),
            state,
        });
    };

    const goToSendPartnerDocuments = (
        allowedDocuments: DocumentType[],
        typeGroup: string,
        signer: Signer
    ) => {
        const state: State = { allowedDocuments, typeGroup, signer };
        history.push({
            pathname: buildUrlWithSigner(
                '/:loanApplicationId/documentacao/envio-documentos-socio/:signerId/',
                match.params.loanApplicationId,
                signer.id
            ),
            state,
        });
    };

    const goToApproved = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/aprovado',
                match.params.loanApplicationId
            )
        );
    };

    // END BORROWER BIZON

    // PARTNER BIZON

    const goToPartnerAntiFraudIdentification = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/socio/identificacao',
                match.params.loanApplicationId
            )
        );
    };

    const goToPartnerAntiFraudStatus = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/socio/identificacao/status',
                match.params.loanApplicationId
            )
        );
    };

    const goToPartnerLivenessStep = () => {
        history.push(
            buildUrl(
                '/:loanApplicationId/socio/identificacao/prova-de-vida',
                match.params.loanApplicationId
            )
        );
    };

    const goToPartnerCreditWorthyStep = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/socio/identificacao/questionario',
                match.params.loanApplicationId
            )
        );
    };

    // END PARTNER BIZON

    const goToShareAntiFraudUrl = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/compartilhe-com-seu-cliente',
                match.params.loanApplicationId
            )
        );
    };

    const goToChooseBankAccount = () => {
        history.replace(
            buildUrl(
                '/:loanApplicationId/documentacao/escolha-conta-bancaria/',
                match.params.loanApplicationId
            )
        );
    };

    const goBack = () => {
        history.goBack();
    };

    const reset = () => {
        setStep(Step.DIRETO);
        history.replace('/');
    };

    const goToGenericError = () => {
        history.replace('/generic-error');
    };

    const borrowerBizonRoutes = {
        goToShareAntiFraudPartner,
        goToSharedLinkSuccessful,
        goToAntiFraudCheckout,
        goToPartnerNotIdentified,
        goToInvalidPartner,
        goToLivenessStep,
        goToCreditWorthyStep,
        goToDocumentCheckout,
        goToSendCompanyDocuments,
        goToSendPartnerDocuments,
        goToApproved,
    };

    const partnerBizonRoutes = {
        goToPartnerAntiFraudIdentification,
        goToPartnerAntiFraudStatus,
        goToPartnerLivenessStep,
        goToPartnerCreditWorthyStep,
    };

    return {
        nextScreen,
        previousScreen,
        goToLoanConfiguration,
        goToLoanInformation,
        goToAddressConfiguration,
        goToBankAccountConfiguration,
        goToProposalConfirmation,
        goToSummary,
        goToFeedback,
        goToPassword,
        goToError,
        goToBndes,
        goToProposalExpired,
        goToLoanDenied,
        goToLoanCancelled,
        goToWelcome,
        goToBizcapitalSite,
        goToPreviousRoute,
        goToAlreadyRegisteredMail,
        goToPreApprovedHome,
        goToShareAntiFraudUrl,
        goToChooseBankAccount,
        goBack,
        reset,
        buildUrl,
        goToGenericError,
        goToNiteroiSite,
        ...borrowerBizonRoutes,
        ...partnerBizonRoutes,
    };
};

export default useNavigation;
